/**
 * Supported locales.
 *
 * This file is the ONLY place in the application that enumerates
 * languages, and it is intentionally tiny. Adding Portuguese means:
 *   1. one row in `public.locales`
 *   2. one entry here
 *   3. the JSON files under `src/i18n/locales/pt-BR/`
 *
 * No component, service or query changes. That is requirement 7, and it
 * is only true because nothing else hardcodes a language anywhere.
 */

export const SUPPORTED_LOCALES = ['en-AU', 'es-CL'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en-AU'

interface LocaleDescriptor {
  /** Shown in the language picker — always in its own language. */
  nativeName: string
  englishName: string
  /** BCP-47 tags a browser might send that should map to this locale. */
  matches: string[]
  direction: 'ltr' | 'rtl'
}

export const LOCALES: Record<Locale, LocaleDescriptor> = {
  'en-AU': {
    nativeName: 'English (Australia)',
    englishName: 'English (Australia)',
    // Any English is closer to en-AU than to Spanish; a British or
    // American visitor should not land in a Spanish interface.
    matches: ['en', 'en-AU', 'en-GB', 'en-NZ', 'en-US', 'en-CA', 'en-IE', 'en-ZA'],
    direction: 'ltr',
  },
  'es-CL': {
    nativeName: 'Español (Chile)',
    englishName: 'Spanish (Chile)',
    // Half this family is Chilean; any Spanish should reach them in
    // Spanish rather than falling back to English.
    matches: ['es', 'es-CL', 'es-AR', 'es-419', 'es-MX', 'es-ES', 'es-PE', 'es-CO'],
    direction: 'ltr',
  },
}

/** Namespaces, loaded lazily so a route only pays for what it renders. */
export const NAMESPACES = [
  'common',
  'auth',
  'people',
  'places',
  'archive',
  'sources',
  'tree',
  'members',
  'onboarding',
  'validation',
  'errors',
] as const
export type Namespace = (typeof NAMESPACES)[number]

export function isSupportedLocale(value: string | null | undefined): value is Locale {
  return SUPPORTED_LOCALES.includes(value as Locale)
}

/**
 * Maps an arbitrary BCP-47 tag to a supported locale.
 *
 * Requirement 6: detect the browser language, use it if supported,
 * otherwise fall back to English (Australia). Matching is done on the
 * language subtag as well as the full tag, because `es-AR` must reach a
 * Spanish speaker even though we do not ship an Argentine locale.
 */
export function resolveLocale(candidate: string | null | undefined): Locale {
  if (!candidate) return DEFAULT_LOCALE

  const normalised = candidate.trim()
  if (isSupportedLocale(normalised)) return normalised

  const lower = normalised.toLowerCase()

  for (const [locale, descriptor] of Object.entries(LOCALES) as [Locale, LocaleDescriptor][]) {
    if (descriptor.matches.some((tag) => tag.toLowerCase() === lower)) return locale
  }

  // Language subtag only: 'es-419' → 'es' → es-CL.
  const language = lower.split('-')[0]
  if (language) {
    for (const [locale, descriptor] of Object.entries(LOCALES) as [Locale, LocaleDescriptor][]) {
      if (descriptor.matches.some((tag) => tag.toLowerCase().split('-')[0] === language)) {
        return locale
      }
    }
  }

  return DEFAULT_LOCALE
}

/** First supported match from the browser's ordered preference list. */
export function detectBrowserLocale(): Locale {
  const candidates = typeof navigator === 'undefined' ? [] : [...navigator.languages]

  for (const candidate of candidates) {
    const resolved = resolveLocale(candidate)
    // resolveLocale never fails, so only accept it when it genuinely
    // matched rather than fell through to the default.
    if (resolved !== DEFAULT_LOCALE || candidate.toLowerCase().startsWith('en')) {
      return resolved
    }
  }

  return DEFAULT_LOCALE
}
